/* ============================================================
   ASTOVA — theme behaviour
   Vanilla JS, no framework. Talks to Shopify's AJAX Cart API.
   ============================================================ */
(function () {
  "use strict";

  /* ---------- one-time bind helper ----------
     Shopify's Theme Editor re-renders a section's HTML via AJAX on every
     block/setting change (it does NOT reload the page, so DOMContentLoaded
     never fires again). All init below used to run exactly once on
     DOMContentLoaded, so after the editor swapped in fresh markup nothing
     re-initialized it: click handlers were missing on the new nodes and,
     more importantly, elements were left stuck in their opacity:0 /
     translated "not yet revealed" state, which is what showed up as a
     blank/white Product Info section while editing.
     bindOnce() lets us safely re-run the same init functions whenever the
     editor reloads a section (see the shopify:section:load listener at the
     bottom) without double-attaching listeners to nodes that already have
     them. */
  function bindOnce(el, key, fn) {
    var flag = "astovaBound" + key;
    if (!el || el.dataset[flag]) return;
    el.dataset[flag] = "1";
    fn();
  }

  /* ---------- Announcement bar rotation ---------- */
  function initAnnouncement(scope) {
    var root = (scope || document).querySelector("[data-astova-announce]");
    if (!root) return;
    bindOnce(root, "Announce", function () {
      var msgs = root.querySelectorAll("[data-msg]");
      if (!msgs.length) return;
      var i = 0;
      function show(idx) {
        msgs.forEach(function (m, mi) {
          m.classList.toggle("is-active", mi === idx);
        });
      }
      show(0);
      var timer = setInterval(function () {
        i = (i + 1) % msgs.length;
        show(i);
      }, 5200);
      root.querySelectorAll("[data-next]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          i = (i + 1) % msgs.length;
          show(i);
          clearInterval(timer);
          timer = setInterval(function () {
            i = (i + 1) % msgs.length;
            show(i);
          }, 5200);
        });
      });
      root.querySelectorAll("[data-prev]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          i = (i - 1 + msgs.length) % msgs.length;
          show(i);
          clearInterval(timer);
          timer = setInterval(function () {
            i = (i + 1) % msgs.length;
            show(i);
          }, 5200);
        });
      });
    });
  }

  /* ---------- Scroll-reveal animations ---------- */
  var scrollRevealIO = null;
  function initScrollReveal(scope) {
    var els = (scope || document).querySelectorAll("[data-animate]");
    if (!els.length) return;
    if (!("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    if (!scrollRevealIO) {
      scrollRevealIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              scrollRevealIO.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.14, rootMargin: "0px 0px -6% 0px" }
      );
    }
    els.forEach(function (el) { scrollRevealIO.observe(el); });
  }

  /* ---------- Generic tabs (used by Science "Why it earns the shelf") ---------- */
  function initTabs(scope) {
    (scope || document).querySelectorAll("[data-tabs]").forEach(function (group) {
      bindOnce(group, "Tabs", function () {
        var triggers = group.querySelectorAll("[data-tab-trigger]");
        var panels = group.querySelectorAll("[data-tab-panel]");
        if (!triggers.length || !panels.length) return;
        triggers.forEach(function (btn) {
          btn.addEventListener("click", function () {
            var target = btn.getAttribute("data-tab-trigger");
            triggers.forEach(function (t) { t.classList.toggle("is-active", t === btn); });
            panels.forEach(function (p) {
              p.classList.toggle("is-active", p.getAttribute("data-tab-panel") === target);
            });
          });
        });
      });
    });
  }

  /* ---------- Count-up numbers (stat blocks) ---------- */
  function initCountUp(scope) {
    var els = (scope || document).querySelectorAll("[data-count-to]");
    if (!els.length || !("IntersectionObserver" in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          var el = entry.target;
          var to = parseFloat(el.getAttribute("data-count-to"));
          var suffix = el.getAttribute("data-count-suffix") || "";
          if (isNaN(to)) return;
          var start = null;
          var duration = 1100;
          function step(ts) {
            if (!start) start = ts;
            var progress = Math.min((ts - start) / duration, 1);
            var eased = 1 - Math.pow(1 - progress, 3);
            var val = Math.round(to * eased);
            el.textContent = val + suffix;
            if (progress < 1) requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        });
      },
      { threshold: 0.5 }
    );
    els.forEach(function (el) {
      bindOnce(el, "CountUp", function () { io.observe(el); });
    });
  }

  /* ---------- Mobile nav drawer ---------- */
  function initMobileNav(scope) {
    var nav = (scope || document).querySelector("[data-mobile-nav]");
    var toggle = (scope || document).querySelector("[data-nav-toggle]");
    if (!nav || !toggle) return;
    bindOnce(toggle, "NavToggle", function () {
      function open() {
        nav.classList.add("is-open");
        toggle.setAttribute("aria-expanded", "true");
        document.body.style.overflow = "hidden";
      }
      function close() {
        nav.classList.remove("is-open");
        toggle.setAttribute("aria-expanded", "false");
        document.body.style.overflow = "";
      }
      toggle.addEventListener("click", open);
      nav.querySelectorAll("[data-nav-close]").forEach(function (el) {
        el.addEventListener("click", close);
      });
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape") close();
      });
    });
  }

  /* ---------- Accordions ---------- */
  function initAccordions(scope) {
    (scope || document).querySelectorAll(".astova-accordion-trigger").forEach(function (btn) {
      bindOnce(btn, "Accordion", function () {
        btn.addEventListener("click", function () {
          var row = btn.closest(".astova-accordion-row");
          if (!row) return;
          var panel = row.querySelector(".astova-accordion-panel");
          var isOpen = row.hasAttribute("open");
          if (isOpen) {
            row.removeAttribute("open");
            if (panel) panel.style.display = "none";
          } else {
            row.setAttribute("open", "");
            if (panel) panel.style.display = "";
          }
        });
      });
    });
  }

  /* ---------- PDP gallery ---------- */
  function initGallery(scope) {
    var root = scope || document;
    var main = root.querySelectorAll("[data-gallery-slide]");
    var thumbs = root.querySelectorAll("[data-gallery-thumb]");
    var dots = root.querySelectorAll("[data-gallery-dot]");
    if (!main.length) return;

    function showSlide(idx) {
      main.forEach(function (slide) {
        slide.style.display = slide.getAttribute("data-gallery-slide") === idx ? "" : "none";
      });
      thumbs.forEach(function (t) {
        t.classList.toggle("is-active", t.getAttribute("data-gallery-thumb") === idx);
      });
      dots.forEach(function (d) {
        d.classList.toggle("is-active", d.getAttribute("data-gallery-dot") === idx);
      });
    }

    thumbs.forEach(function (thumb) {
      bindOnce(thumb, "GalleryThumb", function () {
        thumb.addEventListener("click", function () {
          showSlide(thumb.getAttribute("data-gallery-thumb"));
        });
      });
    });
    dots.forEach(function (dot) {
      bindOnce(dot, "GalleryDot", function () {
        dot.addEventListener("click", function () {
          showSlide(dot.getAttribute("data-gallery-dot"));
        });
      });
    });

    /* Zoom / lightbox */
    var zoomBox = root.querySelector("[data-pdp-zoom]");
    var lightbox = document.querySelector("[data-lightbox]");
    var lightboxImg = lightbox ? lightbox.querySelector("[data-lightbox-img]") : null;
    function openLightbox(src) {
      if (!lightbox || !lightboxImg || !src) return;
      lightboxImg.setAttribute("src", src);
      lightbox.classList.add("is-open");
      document.body.style.overflow = "hidden";
    }
    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove("is-open");
      document.body.style.overflow = "";
    }
    if (zoomBox && zoomBox.classList.contains("is-zoomable")) {
      bindOnce(zoomBox, "Zoom", function () {
        zoomBox.addEventListener("click", function () {
          var visible = zoomBox.querySelector('[data-gallery-slide]:not([style*="display: none"])');
          if (visible) openLightbox(visible.getAttribute("data-zoom-src"));
        });
      });
    }
    var gallery = root.querySelector(".astova-pdp-gallery--grid_2x2");
    if (gallery) {
      thumbs.forEach(function (thumb) {
        bindOnce(thumb, "GalleryZoomThumb", function () {
          thumb.addEventListener("click", function () {
            openLightbox(thumb.getAttribute("data-zoom-src"));
          });
        });
      });
    }
    if (lightbox) {
      bindOnce(lightbox, "Lightbox", function () {
        lightbox.querySelectorAll("[data-lightbox-close]").forEach(function (btn) {
          btn.addEventListener("click", closeLightbox);
        });
        lightbox.addEventListener("click", function (e) {
          if (e.target === lightbox) closeLightbox();
        });
        document.addEventListener("keydown", function (e) {
          if (e.key === "Escape") closeLightbox();
        });
      });
    }
  }

  /* ---------- Buy box: plan + quantity ---------- */
  function initBuyBox(scope) {
    var box = (scope || document).querySelector("[data-buybox]");
    if (!box) return;
    bindOnce(box, "BuyBox", function () {
      var planInputs = box.querySelectorAll("[data-plan-option]");
      var qtyValue = box.querySelector("[data-qty-value]");
      var qty = 1;

      planInputs.forEach(function (opt) {
        opt.addEventListener("click", function () {
          planInputs.forEach(function (o) { o.classList.remove("is-selected"); });
          opt.classList.add("is-selected");
          var sellingPlanId = opt.getAttribute("data-selling-plan-id") || "";
          var hiddenInput = box.querySelector("[data-selling-plan-input]");
          if (hiddenInput) hiddenInput.value = sellingPlanId;
        });
      });

      var dec = box.querySelector("[data-qty-dec]");
      var inc = box.querySelector("[data-qty-inc]");
      if (dec) dec.addEventListener("click", function () {
        qty = Math.max(1, qty - 1);
        if (qtyValue) qtyValue.textContent = qty;
        var input = box.querySelector('input[name="quantity"]');
        if (input) input.value = qty;
      });
      if (inc) inc.addEventListener("click", function () {
        qty = Math.min(9, qty + 1);
        if (qtyValue) qtyValue.textContent = qty;
        var input = box.querySelector('input[name="quantity"]');
        if (input) input.value = qty;
      });
    });
  }

  /* ---------- Cart drawer (Shopify AJAX Cart API) ---------- */
  var Cart = {
    refresh: function () {
      return fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(Cart.render);
    },
    render: function (cart) {
      document.querySelectorAll("[data-cart-count]").forEach(function (el) {
        el.textContent = cart.item_count;
      });
      var body = document.querySelector("[data-drawer-body]");
      var subtotal = document.querySelector("[data-drawer-subtotal]");
      var empty = document.querySelector("[data-drawer-empty]");
      if (!body) return;
      body.innerHTML = "";
      if (!cart.items.length) {
        if (empty) empty.style.display = "";
        if (subtotal) subtotal.textContent = "";
        return;
      }
      if (empty) empty.style.display = "none";
      cart.items.forEach(function (item) {
        var line = document.createElement("div");
        line.className = "astova-drawer-line";
        line.innerHTML =
          '<div class="astova-drawer-thumb"><img src="' + item.image + '" alt="' + item.product_title + '" loading="lazy"></div>' +
          '<div style="flex:1">' +
          '<div class="astova-drawer-title">' + item.product_title + "</div>" +
          '<div class="astova-drawer-meta">' + (item.variant_title || "") + " · Qty " + item.quantity + "</div>" +
          '<div style="font-size:15px;font-weight:500;color:var(--astova-ink);margin-top:12px">' + Cart.money(item.final_line_price) + "</div>" +
          '<button class="astova-drawer-remove" data-remove-line="' + item.key + '">Remove</button>' +
          "</div>";
        body.appendChild(line);
      });
      if (subtotal) subtotal.textContent = Cart.money(cart.total_price);
      body.querySelectorAll("[data-remove-line]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          Cart.updateLine(btn.getAttribute("data-remove-line"), 0);
        });
      });
    },
    money: function (cents) {
      return (window.Shopify && Shopify.formatMoney)
        ? Shopify.formatMoney(cents, window.astovaMoneyFormat || "${{amount}}")
        : "$" + (cents / 100).toFixed(2);
    },
    add: function (formData) {
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      })
        .then(function (r) { return r.json(); })
        .then(function () { return Cart.refresh(); });
    },
    updateLine: function (key, quantity) {
      return fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: quantity }),
      })
        .then(function (r) { return r.json(); })
        .then(Cart.render);
    },
  };

  function openDrawer() {
    var overlay = document.querySelector("[data-drawer-overlay]");
    if (overlay) overlay.classList.add("is-open");
  }
  function closeDrawer() {
    var overlay = document.querySelector("[data-drawer-overlay]");
    if (overlay) overlay.classList.remove("is-open");
  }

  function initCart(scope) {
    var root = scope || document;
    root.querySelectorAll("[data-open-cart]").forEach(function (btn) {
      bindOnce(btn, "OpenCart", function () {
        btn.addEventListener("click", function (e) {
          e.preventDefault();
          openDrawer();
        });
      });
    });
    root.querySelectorAll("[data-close-cart]").forEach(function (btn) {
      bindOnce(btn, "CloseCart", function () {
        btn.addEventListener("click", closeDrawer);
      });
    });

    root.querySelectorAll("[data-product-form]").forEach(function (form) {
      bindOnce(form, "ProductForm", function () {
        form.addEventListener("submit", function (e) {
          e.preventDefault();
          var fd = new FormData(form);
          var submitBtn = form.querySelector('[type="submit"]');
          if (submitBtn) submitBtn.disabled = true;
          Cart.add(fd).then(function () {
            if (submitBtn) submitBtn.disabled = false;
            if (window.astovaCartType === "page") {
              window.location.href = "/cart";
            } else {
              openDrawer();
            }
          }).catch(function () {
            if (submitBtn) submitBtn.disabled = false;
          });
        });
      });
    });

    if (!scope) Cart.refresh();
  }

  /* ---------- Sticky buy bar ---------- */
  var buyBarScrollBound = false;
  function initBuyBar(scope) {
    var bar = (scope || document).querySelector("[data-buybar]");
    if (!bar) return;
    if (buyBarScrollBound) return;
    buyBarScrollBound = true;
    function onScroll() {
      /* Look the bar up fresh on every scroll tick instead of closing over
         the node captured at bind time — the Theme Editor can replace this
         element (e.g. when its own section reloads) and a stale reference
         would silently stop updating the visible bar. */
      var currentBar = document.querySelector("[data-buybar]");
      if (!currentBar) return;
      var overlayOpen = document.querySelector("[data-drawer-overlay].is-open");
      var show = window.scrollY > 1000 && !overlayOpen;
      currentBar.classList.toggle("is-visible", show);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Cookie banner ---------- */
  function initCookieBanner(scope) {
    var banner = (scope || document).querySelector("[data-cookie-banner]");
    if (!banner) return;
    bindOnce(banner, "CookieBanner", function () {
      var dismissed = false;
      try { dismissed = localStorage.getItem("astova_cookies_dismissed") === "1"; } catch (e) {}
      if (!dismissed) {
        banner.classList.add("is-visible");
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { banner.classList.add("is-shown"); });
        });
      }
      banner.querySelectorAll("[data-dismiss-cookies]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          banner.classList.remove("is-shown");
          setTimeout(function () { banner.classList.remove("is-visible"); }, 260);
          try { localStorage.setItem("astova_cookies_dismissed", "1"); } catch (e) {}
        });
      });
    });
  }

  /* ---------- Newsletter (Shopify customer form handles submission natively) ---------- */
  function initNewsletter(scope) {
    (scope || document).querySelectorAll("[data-newsletter-form]").forEach(function (form) {
      bindOnce(form, "Newsletter", function () {
        form.addEventListener("submit", function () {
          var success = document.querySelector("[data-newsletter-success]");
          if (form.querySelector(".newsletter-form__message--success") === null) {
            // Let Shopify's native customer form submit; optimistic UI swap handled via ?customer_posted param on reload.
          }
        });
      });
    });
  }

  function initAll(scope) {
    initAnnouncement(scope);
    initMobileNav(scope);
    initAccordions(scope);
    initGallery(scope);
    initBuyBox(scope);
    initCart(scope);
    initBuyBar(scope);
    initCookieBanner(scope);
    initNewsletter(scope);
    initScrollReveal(scope);
    initTabs(scope);
    initCountUp(scope);
  }

  document.addEventListener("DOMContentLoaded", function () {
    initAll();
  });

  /* ---------- Shopify Theme Editor: re-init after a section/block reload ----------
     The editor swaps a section's markup in place with an AJAX request and
     fires "shopify:section:load" on the new section element — it does not
     reload the page, so nothing above would otherwise ever run again for
     that section's new DOM nodes. Every init function above is now
     idempotent (bindOnce / observer-based), so re-running the full suite
     scoped to just the reloaded section is safe and keeps accordions, the
     PDP gallery, the buy box, the sticky buy bar and scroll-reveal working
     immediately after any edit — instead of only "fixing itself" on Save
     or a manual refresh. This listener only ever fires inside the Theme
     Editor / theme preview, so it has no effect on the live storefront. */
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();
